import _ from 'lodash';
import moment from 'moment-timezone';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import TimeZoneSelector from '@/core/TimeZoneSelector.molecule';
import { getTimeRounded } from '@/datetime/dateTime.utilities';
import { AutoUpdateTimeScheduleProperties } from '@/schedule/AutoUpdateTimeScheduleProperties.molecule';
import { determineInvalidEntries } from '@/schedule/schedule.utilities';
import type { Timezone } from '@/datetime/timezone.service';
import type { AutoUpdateTimeScheduleEntry } from '@/schedule/schedule.types';

interface AutoUpdateTimeScheduleProps {
  timezone?: Timezone;
  setTimezone?: (timezone: Timezone) => void;
  entries?: AutoUpdateTimeScheduleEntry[];
  setEntries: (entries: AutoUpdateTimeScheduleEntry[]) => void;
}

export const AutoUpdateTimeSchedule = ({
  timezone,
  setTimezone = _.noop,
  entries,
  setEntries,
}: AutoUpdateTimeScheduleProps) => {
  const sortByLocalTime = (entries: AutoUpdateTimeScheduleEntry[]) => {
    return _.chain(entries).sortBy(['time']).value();
  };

  const setAllEntries = (entries: AutoUpdateTimeScheduleEntry[]) => {
    setEntries(entries);
    setLocalTimeEntries(entries);
  };

  const onEntryChange = (targetIndex: number, updatedEntry: AutoUpdateTimeScheduleEntry) => {
    const updatedEntries = _.map(localTimeEntries, (existingEntry, index) =>
      index === targetIndex ? updatedEntry : existingEntry,
    );
    setAllEntries(updatedEntries);
  };

  const insertDefaultTimeEntry = () => {
    const updatedEntries = [
      ..._.cloneDeep(localTimeEntries),
      {
        time: getTimeRounded(moment().subtract(15, 'minutes'), timezone),
      },
    ];
    setAllEntries(updatedEntries);
  };

  const removeTimeEntry = (targetIndex: number) => {
    const updatedEntries = localTimeEntries.filter((entry, index) => index !== targetIndex);
    setAllEntries(updatedEntries);
  };

  const { t } = useTranslation();
  const [localTimeEntries, setLocalTimeEntries] = useState(sortByLocalTime(entries as AutoUpdateTimeScheduleEntry[]));

  const isEntryInvalid = determineInvalidEntries(localTimeEntries);

  return (
    <div className="flexRowContainer mb15" data-testid="autoUpdateTimeSchedule">
      <div className="flexColumnContainer flexNoShrink mr15">
        <div className="flexRowContainer">
          <span className="reportAutoUpdateTimeStrategy mr5">
            {t('REPORT.MODAL.AUTO_UPDATE.TIME.STRATEGIES.UPDATE_AT')}
          </span>
          {_.map(localTimeEntries, (entry, index) => (
            <AutoUpdateTimeScheduleProperties
              key={`entry${index}`}
              isInvalid={isEntryInvalid[index]}
              showRemoveIcon={localTimeEntries.length > 1}
              entry={entry}
              onChange={(entry) => onEntryChange(index, entry)}
              onClickRemove={() => removeTimeEntry(index)}
            />
          ))}
        </div>

        {timezone && (
          <div className="flexRowContainer ml50 mb15">
            <span className="reportAutoUpdateTimeStrategy mr5">{t('REPORT.MODAL.AUTO_UPDATE.TIME.TIME_ZONE')}</span>
            <TimeZoneSelector extraClassNames="width-220" timezone={timezone} onSelect={setTimezone} />
          </div>
        )}
      </div>
      <label>
        <FakeLink extraClassNames="link-no-focus link-no-underline" onClick={insertDefaultTimeEntry}>
          {t('REPORT.MODAL.AUTO_UPDATE.TIME.ADD_ANOTHER_TIME')}
        </FakeLink>
      </label>
    </div>
  );
};
